import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { BookOpen } from "lucide-react";
import ToastContainer, { showToast } from "./Toast";
import { STORAGE_KEYS, readStorage, saveStorage, addActivity, getSchoolSettings, loadGoals, saveGoals, addActivityAsync, generateGoalsFromActions, generateGoalsFromSQAAF, getSQAAFStatus } from "./utils";
import StaffSelect from "./StaffSelect";

const TAG = {
  green: "bg-[#eaf8f1] text-[#2d9b68] border-[#cfe8dc]",
  blue: "bg-[#eef6fc] text-[#2d81c1] border-[#cfe3f3]",
  orange: "bg-[#fff4eb] text-[#f47c20] border-[#f7c9a1]",
  yellow: "bg-[#fff7e7] text-[#d58d1f] border-[#f4deab]",
  red: "bg-[#fff0f0] text-[#d95353] border-[#f4c5c5]",
};

function Tag({ cls = "blue", children }) {
  return (
    <span className={`inline-flex items-center gap-[6px] px-[10px] py-[6px] rounded-full text-[11px] font-semibold border whitespace-nowrap ${TAG[cls] || TAG.blue}`}>
      {children}
    </span>
  );
}

function Window({ title, note, headerRight, children }) {
  return (
    <section className="bg-white border border-[#e6ecf3] rounded-[26px] shadow-[0_16px_36px_rgba(19,31,47,.07)] overflow-hidden">
      <div className="px-5 py-[18px] border-b border-[#e6ecf3] flex items-center justify-between gap-3">
        <div>
          <div className="text-[16px] font-semibold text-[#233445] mb-[2px]">{title}</div>
          {note && <div className="text-[12px] text-[#617587]">{note}</div>}
        </div>
        {headerRight && <div className="flex flex-wrap gap-[10px]">{headerRight}</div>}
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

const EMPTY_GOAL = { category: "", priority: "", title: "", description: "", owner: "", timeline: "", kpi: "", status: "" };

function calculateCompletion(goals) {
  const total = goals.length;
  const highPriority = goals.filter(g => g.priority === "High").length;
  const withOwner = goals.filter(g => g.owner).length;
  const withKpi = goals.filter(g => g.kpi).length;
  const withTimeline = goals.filter(g => g.timeline).length;

  let filled = 0;
  if (total > 0) filled++;
  if (total >= 3) filled++;
  if (highPriority > 0) filled++;
  if (withOwner >= Math.min(total, 2) && total > 0) filled++;
  if (withKpi >= Math.min(total, 2) && total > 0) filled++;
  if (withTimeline >= Math.min(total, 2) && total > 0) filled++;
  if (total >= 5) filled++;
  if (goals.some(g => g.status === "Planned" || g.status === "In Progress")) filled++;

  return Math.round((filled / 8) * 100);
}

export default function Goals() {
  const settings = getSchoolSettings();
  const [goals, setGoals] = useState(() => {
    const stored = readStorage(STORAGE_KEYS.goals, null);
    return Array.isArray(stored?.goals) ? stored.goals : [];
  });
  const [form, setForm] = useState(EMPTY_GOAL);
  const [editingIndex, setEditingIndex] = useState(null);
  const [activeTab, setActiveTab] = useState("entry");
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("");
  const [quickCategory, setQuickCategory] = useState("Academic Improvement");
  const [generating, setGenerating] = useState(false);
  const [sqaafAuditId, setSqaafAuditId] = useState(null);
  const [showSQAAFBanner, setShowSQAAFBanner] = useState(false);

  useEffect(() => {
    loadGoals([]).then(apiGoals => {
      if (apiGoals.length > 0) setGoals(apiGoals);
      const cached = readStorage(STORAGE_KEYS.goals, null);
      const storedAuditId = cached?.sqaaf_audit_id || apiGoals?.sqaaf_audit_id || null;
      setSqaafAuditId(storedAuditId);
      getSQAAFStatus().then(status => {
        if (status?.has_audit && storedAuditId && status.audit_id && status.audit_id !== storedAuditId) {
          setShowSQAAFBanner(true);
        }
      });
    });
  }, []);

  function persistGoals(list, label = "Goals saved", auditId = null) {
    saveGoals(list, auditId);
    addActivityAsync("Goals", label, "Saved", "green");
  }

  async function handleGenerateFromActions() {
    setGenerating(true);
    try {
      const suggested = await generateGoalsFromActions();
      if (!suggested || suggested.length === 0) {
        showToast("No action plans found to generate goals from. Add action plans first.", "warn");
        return;
      }
      const mapped = suggested.map(g => ({
        category: g.category || "General",
        priority: g.priority || "Medium",
        title: g.goal || "",
        description: g.goal || "",
        owner: "",
        timeline: g.target_date || "",
        kpi: "",
        status: "Planned",
      }));
      const merged = [...goals, ...mapped];
      persistGoals(merged, "Goals auto-generated from action plans");
      setGoals(merged);
      showToast(`${mapped.length} goal(s) generated from action plans.`, "success");
    } catch (err) {
      showToast("Failed to generate goals. Try again.", "error");
    } finally {
      setGenerating(false);
    }
  }

  async function handleGenerateFromSQAAF() {
    setGenerating(true);
    try {
      const res = await generateGoalsFromSQAAF();
      if (!res || !res.goals || res.goals.length === 0) {
        showToast("No SQAAF data found. Complete a SQAAF audit first.", "warn");
        return;
      }
      const mapped = res.goals.map(g => ({
        category: g.category || "General",
        priority: g.priority || "Medium",
        title: g.goal || "",
        description: g.goal || "",
        owner: "",
        timeline: g.target_date || "",
        kpi: "",
        status: "Planned",
        sqaaf_audit_id: res.sqaaf_audit_id,
      }));
      const merged = [...goals, ...mapped];
      setSqaafAuditId(res.sqaaf_audit_id);
      setShowSQAAFBanner(false);
      persistGoals(merged, "Goals generated from SQAAF", res.sqaaf_audit_id);
      setGoals(merged);
      showToast(`${mapped.length} goal(s) generated from SQAAF domains.`, "success");
    } catch (err) {
      showToast("Failed to generate goals from SQAAF. Try again.", "error");
    } finally {
      setGenerating(false);
    }
  }

  function handleAddOrUpdate() {
    if (!form.category || !form.priority || !form.title.trim() || !form.description.trim()) {
      showToast("Goal Category, Priority, Goal Title, and Description are required.", "error");
      return;
    }
    let updated;
    if (editingIndex !== null) {
      updated = goals.map((g, i) => i === editingIndex ? form : g);
      persistGoals(updated, "Goal updated");
      showToast("Goal updated.", "success");
    } else {
      updated = [...goals, form];
      persistGoals(updated, "Goal added");
      showToast("Goal added.", "success");
    }
    setGoals(updated);
    setForm(EMPTY_GOAL);
    setEditingIndex(null);
  }

  function handleEdit(i) {
    setForm(goals[i]);
    setEditingIndex(i);
    setActiveTab("entry");
  }

  function handleDelete(i) {
    const updated = goals.filter((_, idx) => idx !== i);
    persistGoals(updated, "Goal deleted");
    setGoals(updated);
    showToast("Goal deleted.", "warn");
  }

  function handleSaveDraft() {
    if (!goals.length) { showToast("Add at least one goal before saving draft.", "error"); return; }
    persistGoals(goals, "Goals draft saved");
    showToast("Goals draft saved.", "success");
  }

  function handleFillSample() {
    const sample = [
      { category: "Academic Improvement", priority: "High", title: "Improve teaching consistency across middle grades", description: "Standardize classroom quality and instructional delivery across all sections in middle school.", owner: "Academic Coordinator", timeline: "6 Months", kpi: "Average classroom observation score improves by 20%", status: "Planned" },
      { category: "Parent Engagement", priority: "High", title: "Strengthen parent communication and trust", description: "Build a more structured and transparent parent communication rhythm focused on student growth.", owner: "Principal", timeline: "3 Months", kpi: "Parent satisfaction feedback score improves by 15%", status: "Planned" },
      { category: "Operational Excellence", priority: "Medium", title: "Improve review-to-action follow-through", description: "Ensure that meetings and reviews result in clear action points, timelines, and accountability.", owner: "Vice Principal", timeline: "6 Months", kpi: "90% of review action points closed within committed timeline", status: "In Progress" },
      { category: "Culture & Discipline", priority: "Medium", title: "Build stronger student ownership and learning culture", description: "Shift from compliance-only discipline to a more reflective and responsible student culture.", owner: "Student Wellbeing Committee", timeline: "1 Year", kpi: "Visible improvement in student participation and classroom responsibility indicators", status: "Planned" },
    ];
    persistGoals(sample, "Sample goals data loaded");
    setGoals(sample);
    showToast("Sample Goals content loaded.", "success");
  }

  function handleClear() {
    if (!window.confirm("This will clear all saved data in Goals. Continue?")) return;
    saveGoals([]);
    addActivityAsync("Goals", "Module cleared", "Cleared", "red");
    setGoals([]);
    setForm(EMPTY_GOAL);
    setEditingIndex(null);
    showToast("Goals module cleared.", "warn");
  }

  function handleQuickCategory(e) {
    setQuickCategory(e.target.value);
    setForm(f => ({ ...f, category: e.target.value }));
    showToast(`Goal category preselected as ${e.target.value}.`, "success");
  }

  const completion = calculateCompletion(goals);
  const highPriority = goals.filter(g => g.priority === "High").length;
  const readiness = completion >= 85 ? "High" : completion >= 55 ? "Medium" : "Low";
  const interpretation = completion >= 85
    ? "The school now has a solid goal structure and can move into action planning with good clarity."
    : completion >= 55
    ? "The school has a useful goal structure, but some goals still need stronger ownership, KPIs, or timelines."
    : "The school still needs clearer goals before strong action planning can begin.";

  const GOAL_CATEGORY_OPTIONS = ["Academic Improvement","Operational Excellence","Culture & Discipline","Parent Engagement","Leadership & Governance"];
  const GOAL_PRIORITY_OPTIONS = ["High","Medium","Low"];
  const GOAL_STATUS_OPTIONS = ["Planned","In Progress","Completed"];

  const filteredGoals = goals.filter(g =>
    (!searchQuery.trim() || g.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (g.owner || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (g.kpi || "").toLowerCase().includes(searchQuery.toLowerCase())) &&
    (!categoryFilter || g.category === categoryFilter) &&
    (!priorityFilter || g.priority === priorityFilter) &&
    (!statusFilter || g.status === statusFilter) &&
    (!ownerFilter || g.owner === ownerFilter)
  );

  const highPriorityGoals = goals.filter(g => g.priority === "High");
  const owners = [...new Set(goals.map(g => g.owner).filter(Boolean))];
  const timelines = [...new Set(goals.map(g => g.timeline).filter(Boolean))];
  const kpis = goals.map(g => g.kpi).filter(Boolean);

  const tabBtn = (tab, label) => (
    <button
      onClick={() => setActiveTab(tab)}
      className={`px-5 py-[10px] rounded-[12px] text-[13px] font-semibold transition-colors ${
        activeTab === tab ? "bg-[#f47c20] text-white" : "bg-white text-[#617587] border border-[#e6ecf3] hover:bg-[#eef6fc] hover:text-[#2d81c1]"
      }`}
    >
      {label}
    </button>
  );

  return (

    <>
      <div className="flex-1 min-w-0 flex flex-col border-l-2 border-slate-200 ml-1">

        <div className="flex justify-center pl-10 pr-6 py-6">
          <main className="w-full font-[poppins]">
            <section className="mb-8">
              <div className="text-center px-4 py-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6 bg-[#D67C2C]">
                  <BookOpen className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-5xl font-bold text-[#D67C2C] mb-4">Strategic Goals</h1>
                <p className="text-[#1D3469] text-[18px] sm:text-[20px] font-medium max-w-5xl mx-auto px-4 leading-[1.4]">
                  Translate SWOT insights into clear, owned, time-bound, and measurable school goals.
                </p>
              </div>
            </section>

            <div className="mb-[18px] px-5 py-4 rounded-[16px] bg-[#eef6fc] border border-[#cfe3f3]">
              <div className="text-[13px] text-[#617587] max-w-[980px]">
                Each goal here should become the basis for action plans, execution timelines, and monitoring reviews.
              </div>
            </div>

            {/* SQAAF Banner */}
            {showSQAAFBanner && (
              <div className="mb-[18px] flex items-center justify-between gap-4 px-5 py-4 rounded-[16px] bg-[#fff7e7] border border-[#f4deab]">
                <div className="flex items-center gap-3">
                  <span className="text-[18px]">⚠️</span>
                  <div>
                    <div className="text-[13px] font-semibold text-[#d58d1f]">Your SQAAF has a newer audit cycle</div>
                    <div className="text-[12px] text-[#9a7233]">The goals below were generated from an older SQAAF audit. Regenerate to reflect the latest data.</div>
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button onClick={() => setShowSQAAFBanner(false)} className="px-3 py-[7px] rounded-[10px] border border-[#f4deab] bg-white text-[#d58d1f] text-[12px] font-semibold hover:bg-[#fff4eb] transition-colors">Dismiss</button>
                  <button onClick={handleGenerateFromSQAAF} disabled={generating} className="px-3 py-[7px] rounded-[10px] bg-[#d58d1f] text-white text-[12px] font-semibold hover:bg-[#b8781a] transition-colors disabled:opacity-50">{generating ? "Generating…" : "Regenerate Goals"}</button>
                </div>
              </div>
            )}

            {/* Stats */}
            <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {[
                { label: "Module Completion", icon: "📌", iconCls: "bg-[#fff4eb] text-[#f47c20] border-[#f7c9a1]", value: `${completion}%`, meta: "Based on goal quality and coverage", bar: true },
                { label: "Total Goals", icon: "🎯", iconCls: "bg-[#eef6fc] text-[#2d81c1] border-[#cfe3f3]", value: goals.length, meta: "Strategic goals currently created" },
                { label: "High Priority Goals", icon: "🔥", iconCls: "bg-[#fff0f0] text-[#d95353] border-[#f4c5c5]", value: highPriority, meta: "Goals marked as high priority" },
                { label: "Goal Readiness", icon: "📍", iconCls: "bg-[#fff7e7] text-[#d58d1f] border-[#f4deab]", value: readiness, meta: "How ready the goals are for action planning" },
              ].map((s, i) => (
                <div key={i} className="bg-white rounded-xl shadow-md border border-gray-200 p-6 hover:shadow-lg transition-all duration-300 min-h-[130px] flex flex-col justify-between">
                  <div className="flex items-center justify-between gap-[10px] mb-3">
                    <span className="text-[12px] text-[#617587] font-medium">{s.label}</span>
                    <div className={`w-[42px] h-[42px] rounded-[14px] grid place-items-center text-[18px] border ${s.iconCls}`}>{s.icon}</div>
                  </div>
                  <div className="text-[28px] font-bold leading-none text-[#233445] mb-[6px]">{s.value}</div>
                  <div className="text-[12px] text-[#617587]">{s.meta}</div>
                  {s.bar && (
                    <div className="w-full h-[6px] rounded-full bg-[#edf2f8] overflow-hidden mt-2">
                      <div className="h-full rounded-full bg-gradient-to-r from-[#f47c20] to-[#2d81c1] transition-all" style={{ width: `${completion}%` }} />
                    </div>
                  )}
                </div>
              ))}
            </section>

            {/* Tabs */}
            <div className="flex gap-3 mb-[18px]">
              {tabBtn("entry", "Add Goal")}
              {tabBtn("list", "Goals List")}
              {tabBtn("preview", "Live Preview")}
            </div>

            {/* Entry Tab */}
            {activeTab === "entry" && (
              <div className="grid gap-[18px]" style={{ gridTemplateColumns: "1.2fr .8fr" }}>
                <Window
                  title="Strategic Goal Entry"
                  note="Add one goal at a time with category, owner, KPI, and timeline."
                  headerRight={
                    <>
                      <button onClick={() => { setForm(EMPTY_GOAL); setEditingIndex(null); }} className="min-h-[44px] px-4 bg-white text-[#233445] border border-[#e6ecf3] rounded-[12px] text-[13px] font-semibold hover:bg-[#eef6fc] hover:text-[#2d81c1] hover:border-[#cfe3f3] transition-colors">Reset Input</button>
                      <button onClick={handleAddOrUpdate} className="min-h-[44px] px-4 bg-[#f47c20] text-white rounded-[12px] text-[13px] font-semibold hover:bg-[#e46c13] transition-colors">{editingIndex !== null ? "Update Goal" : "Add Goal"}</button>
                    </>
                  }
                >
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1">
                      <label className="text-[12px] font-semibold text-[#233445]">Goal Category</label>
                      <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="h-[42px] border border-[#e6ecf3] rounded-[10px] px-3 text-[13px] text-[#233445] bg-white outline-none focus:border-[#2d81c1]">
                        <option value="">Select category</option>
                        {["Academic Improvement","Operational Excellence","Culture & Discipline","Parent Engagement","Leadership & Governance"].map(c => <option key={c}>{c}</option>)}
                      </select>
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[12px] font-semibold text-[#233445]">Priority</label>
                      <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))} className="h-[42px] border border-[#e6ecf3] rounded-[10px] px-3 text-[13px] text-[#233445] bg-white outline-none focus:border-[#2d81c1]">
                        <option value="">Select priority</option>
                        {["High","Medium","Low"].map(p => <option key={p}>{p}</option>)}
                      </select>
                    </div>
                    <div className="flex flex-col gap-1 col-span-2">
                      <label className="text-[12px] font-semibold text-[#233445]">Goal Title</label>
                      <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Example: Improve teaching consistency across middle grades" className="h-[42px] border border-[#e6ecf3] rounded-[10px] px-3 text-[13px] text-[#233445] bg-white outline-none focus:border-[#2d81c1]" />
                    </div>
                    <div className="flex flex-col gap-1 col-span-2">
                      <label className="text-[12px] font-semibold text-[#233445]">Goal Description</label>
                      <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="What exactly does the school want to achieve through this goal?" rows={3} className="border border-[#e6ecf3] rounded-[10px] px-3 py-2 text-[13px] text-[#233445] bg-white outline-none focus:border-[#2d81c1] resize-y" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[12px] font-semibold text-[#233445]">Owner</label>
                      <StaffSelect value={form.owner} onChange={v => setForm(f => ({ ...f, owner: v }))} placeholder="Search staff or type a name…" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[12px] font-semibold text-[#233445]">Timeline</label>
                      <select value={form.timeline} onChange={e => setForm(f => ({ ...f, timeline: e.target.value }))} className="h-[42px] border border-[#e6ecf3] rounded-[10px] px-3 text-[13px] text-[#233445] bg-white outline-none focus:border-[#2d81c1]">
                        <option value="">Select timeline</option>
                        {["3 Months","6 Months","1 Year","Ongoing"].map(t => <option key={t}>{t}</option>)}
                      </select>
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[12px] font-semibold text-[#233445]">KPI / Success Metric</label>
                      <input value={form.kpi} onChange={e => setForm(f => ({ ...f, kpi: e.target.value }))} placeholder="Example: classroom observation score improves by 20%" className="h-[42px] border border-[#e6ecf3] rounded-[10px] px-3 text-[13px] text-[#233445] bg-white outline-none focus:border-[#2d81c1]" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[12px] font-semibold text-[#233445]">Status</label>
                      <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className="h-[42px] border border-[#e6ecf3] rounded-[10px] px-3 text-[13px] text-[#233445] bg-white outline-none focus:border-[#2d81c1]">
                        <option value="">Select status</option>
                        {["Planned","In Progress","Completed"].map(s => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                </Window>

                <Window title="Why goals matter" note="How this page improves execution.">
                  {[
                    { title: "Turns strategy into targets", text: "A SWOT tells the school what matters. Goals define what the school will now try to achieve in response." },
                    { title: "Creates direction for action plans", text: "The next page should convert these goals into specific activities, owners, and deadlines." },
                    { title: "Improves measurable thinking", text: "Every strong goal should have some form of success metric so later monitoring is not vague." },
                    { title: "Strengthens accountability", text: "A goal without an owner or timeline is not yet a usable strategic objective." },
                  ].map((info, i) => (
                    <div key={i} className="border border-[#e6ecf3] bg-[#fbfcfe] rounded-[14px] p-4 mb-3 last:mb-0">
                      <div className="text-[14px] font-semibold text-[#233445] mb-[6px]">{info.title}</div>
                      <div className="text-[12px] text-[#617587] leading-[1.7]">{info.text}</div>
                    </div>
                  ))}
                </Window>
              </div>
            )}

            {/* Goals List Tab */}
            {activeTab === "list" && (() => {
              const incomplete = goals.filter(g => !g.owner || !g.kpi);
              return (
                <div className="flex flex-col gap-[18px]">
                  {/* Incomplete banner */}
                  {incomplete.length > 0 && (
                    <div className="flex items-center justify-between gap-4 px-5 py-4 rounded-[16px] bg-[#eef6fc] border border-[#cfe3f3]">
                      <div className="flex items-center gap-3">
                        <span className="text-[18px]">✏️</span>
                        <div>
                          <div className="text-[13px] font-semibold text-[#2d81c1]">{incomplete.length} goal{incomplete.length > 1 ? "s are" : " is"} missing Owner or KPI</div>
                          <div className="text-[12px] text-[#4a7fa5]">Click <strong>Edit</strong> on any goal to assign an owner, add a KPI, and set the status. Goals without owners can't be executed.</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Full-width goals table */}
                  <Window title={`Goals List (${goals.length})`} note="Click Edit on any row to fill in missing owner, KPI, or status.">
                    {!goals.length ? (
                      <div className="text-center py-8 text-[13px] text-[#617587]">No goals added yet. Use "⚡ From SQAAF" or "Add Goal" tab.</div>
                    ) : (
                      <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
                        <div className="flex flex-col gap-1">
                          <label className="text-[11px] font-semibold text-[#617587]">Search</label>
                          <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search title, owner, KPI…" className="h-[38px] border border-[#e6ecf3] rounded-[10px] px-3 text-[12px] text-[#233445] bg-white outline-none focus:border-[#2d81c1]" />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-[11px] font-semibold text-[#617587]">Category</label>
                          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="h-[38px] border border-[#e6ecf3] rounded-[10px] px-3 text-[12px] text-[#233445] bg-white outline-none focus:border-[#2d81c1]">
                            <option value="">All Categories</option>
                            {GOAL_CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-[11px] font-semibold text-[#617587]">Priority</label>
                          <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)} className="h-[38px] border border-[#e6ecf3] rounded-[10px] px-3 text-[12px] text-[#233445] bg-white outline-none focus:border-[#2d81c1]">
                            <option value="">All Priorities</option>
                            {GOAL_PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                          </select>
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-[11px] font-semibold text-[#617587]">Status</label>
                          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="h-[38px] border border-[#e6ecf3] rounded-[10px] px-3 text-[12px] text-[#233445] bg-white outline-none focus:border-[#2d81c1]">
                            <option value="">All Statuses</option>
                            {GOAL_STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-[11px] font-semibold text-[#617587]">Owner</label>
                          <select value={ownerFilter} onChange={e => setOwnerFilter(e.target.value)} className="h-[38px] border border-[#e6ecf3] rounded-[10px] px-3 text-[12px] text-[#233445] bg-white outline-none focus:border-[#2d81c1]">
                            <option value="">All Owners</option>
                            {owners.map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                        </div>
                      </div>
                      {filteredGoals.length === 0 ? (
                        <div className="text-center py-8 text-[13px] text-[#617587]">No goals match the current filters.</div>
                      ) : (
                      <div className="border border-[#e6ecf3] rounded-[18px] overflow-hidden">
                        <table className="w-full border-collapse">
                          <thead>
                            <tr>
                              {["#","Goal","Category","Priority","Owner","KPI","Timeline","Status","Actions"].map(h => (
                                <th key={h} className="bg-[#fafbfd] px-3 py-[12px] text-left text-[11px] text-[#617587] font-semibold border-b border-[#e6ecf3] whitespace-nowrap">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {filteredGoals.map((goal, i) => {
                              const pCls = goal.priority === "High" ? "red" : goal.priority === "Medium" ? "orange" : "blue";
                              const isIncomplete = !goal.owner || !goal.kpi;
                              const realIdx = goals.indexOf(goal);
                              return (
                                <tr key={i} className={`hover:bg-[#fcfdff] ${isIncomplete ? "bg-[#fffbf5]" : ""}`}>
                                  <td className="px-3 py-3 text-[12px] text-[#8ea0b1] border-b border-[#eef2f7]">{realIdx + 1}</td>
                                  <td className="px-3 py-3 text-[13px] border-b border-[#eef2f7] max-w-[220px]">
                                    <div className="font-semibold text-[#233445] leading-snug">{goal.title}</div>
                                    {isIncomplete && <span className="inline-block mt-1 text-[10px] font-semibold text-[#f47c20] bg-[#fff4eb] px-2 py-[2px] rounded-full border border-[#f7c9a1]">⚠ Incomplete</span>}
                                  </td>
                                  <td className="px-3 py-3 border-b border-[#eef2f7]"><Tag cls="blue">{goal.category || "—"}</Tag></td>
                                  <td className="px-3 py-3 border-b border-[#eef2f7]"><Tag cls={pCls}>{goal.priority || "—"}</Tag></td>
                                  <td className="px-3 py-3 text-[13px] border-b border-[#eef2f7]">
                                    {goal.owner ? <span className="text-[#233445]">{goal.owner}</span> : <span className="text-[#d95353] text-[12px] font-medium">Not set</span>}
                                  </td>
                                  <td className="px-3 py-3 text-[12px] text-[#617587] border-b border-[#eef2f7] max-w-[160px]">
                                    {goal.kpi ? <span className="text-[#233445]">{goal.kpi}</span> : <span className="text-[#d95353] font-medium">Not set</span>}
                                  </td>
                                  <td className="px-3 py-3 border-b border-[#eef2f7] whitespace-nowrap"><Tag cls="green">{goal.timeline || "—"}</Tag></td>
                                  <td className="px-3 py-3 border-b border-[#eef2f7]"><Tag cls="orange">{goal.status || "—"}</Tag></td>
                                  <td className="px-3 py-3 border-b border-[#eef2f7]">
                                    <div className="flex gap-2">
                                      <button onClick={() => { handleEdit(realIdx); window.scrollTo({ top: 0, behavior: "smooth" }); }} className={`px-3 py-1 rounded-[8px] text-[11px] font-semibold transition-colors ${isIncomplete ? "bg-[#f47c20] text-white hover:bg-[#e46c13]" : "bg-[#eef6fc] text-[#2d81c1] hover:bg-[#cfe3f3]"}`}>
                                        {isIncomplete ? "Complete" : "Edit"}
                                      </button>
                                      <button onClick={() => handleDelete(realIdx)} className="px-3 py-1 rounded-[8px] bg-[#fff0f0] text-[#d95353] text-[11px] font-semibold hover:bg-[#f4c5c5] transition-colors">Delete</button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      )}
                      </>
                    )}
                  </Window>
                </div>
              );
            })()}

            {/* Preview Tab */}
            {activeTab === "preview" && (
              <div className="grid grid-cols-2 gap-[18px]">
                <Window title="Live Goals Summary" note="What the current goal structure is showing.">
                  {[
                    { label: "Priority Direction", value: highPriorityGoals.length ? `${highPriorityGoals.length} high-priority goal(s), including ${highPriorityGoals.slice(0,2).map(g => g.title).join(" and ")}.` : "No goals saved yet." },
                    { label: "Ownership Snapshot", value: owners.length ? `Goals are currently distributed across ${owners.length} owner(s), including ${owners.slice(0,3).join(", ")}.` : "No goals saved yet." },
                    { label: "Timeline Snapshot", value: timelines.length ? `Goals currently span ${timelines.join(", ")}.` : "No goals saved yet." },
                    { label: "Measurement Snapshot", value: kpis.length ? `${kpis.length} goal(s) currently include measurable KPI or success metric statements.` : "No goals saved yet." },
                  ].map((p, i) => (
                    <div key={i} className="border border-[#e6ecf3] bg-[#fbfcfe] rounded-[14px] p-4 mb-3 last:mb-0">
                      <div className="text-[11px] font-semibold text-[#8ea0b1] uppercase tracking-wider mb-2">{p.label}</div>
                      <div className="text-[13px] text-[#233445]">{p.value}</div>
                    </div>
                  ))}
                </Window>

                <Window title="Readiness Interpretation" note="How ready the school is for action plans.">
                  {[
                    { title: "Goal quality", text: interpretation },
                    { title: "Why this matters next", text: "The Action Plans module should directly break these goals into tasks, owners, sequences, and execution timelines." },
                    { title: "Next connected page", text: "After this, move to Action Plans so every goal can be translated into concrete workstreams and implementation steps." },
                  ].map((info, i) => (
                    <div key={i} className="border border-[#e6ecf3] bg-[#fbfcfe] rounded-[14px] p-4 mb-3 last:mb-0">
                      <div className="text-[14px] font-semibold text-[#233445] mb-[6px]">{info.title}</div>
                      <div className="text-[12px] text-[#617587] leading-[1.7]">{info.text}</div>
                    </div>
                  ))}
                </Window>
              </div>
            )}

            <section className="bg-white rounded-xl shadow-md border border-gray-200 p-6 mt-8 mb-8">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-[#1D3469]">Module Actions</h3>
                  <p className="text-sm text-gray-500">Save and manage module actions from one place.</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <button onClick={handleSaveDraft} className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-medium">
                    Save Draft
                  </button>
                  <button onClick={() => { persistGoals(goals); showToast("Goals saved.", "success"); }} className="bg-[#D67C2C] hover:bg-[#c06a25] text-white px-6 py-3 rounded-lg font-medium transition-colors">
                    Save Module
                  </button>
                  <button onClick={handleFillSample} className="border border-[#D67C2C] text-[#D67C2C] px-4 py-2 rounded-lg hover:bg-[#fff4eb] transition-colors font-medium">
                    Load Sample
                  </button>
                  <button onClick={handleClear} className="border border-red-200 text-red-600 px-4 py-2 rounded-lg hover:bg-red-50 transition-colors font-medium">
                    Clear Module
                  </button>
                  <Link to="/sdp/dashboard" className="bg-[#1D3469] hover:bg-[#16284f] text-white px-6 py-3 rounded-lg font-medium transition-colors">
                    Back to Dashboard
                  </Link>
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>

      <ToastContainer />
    </>
  );
}
